import {parseUrl} from 'utils';
import {ActionTypes as uiActionTypes} from 'store/ui/actions';
import {ActionTypes as configActionTypes} from 'store/config/actions';
import {ActionTypes as videoActionTypes} from 'store/video/actions';
import {ActionTypes as elementListActionTypes} from 'store/element-list/actions';
import {ActionTypes as searchActionTypes} from 'store/search/actions';

const ActionTypes = Object.assign(
    {},
    uiActionTypes,
    configActionTypes,
    videoActionTypes,
    elementListActionTypes,
    searchActionTypes,
);

const eventFactory = {};

// Initialization

eventFactory[ActionTypes.PARSE_OPTIONS] = () => ({
    category: 'Initialization',
    action: 'Video Detected',
});

eventFactory[ActionTypes.ELEMENT_LIST_MANIFEST_LOADED] = () => ({
    category: 'Initialization',
    action: 'Manifest Loaded',
});

eventFactory[ActionTypes.INITIAL_RENDER] = () => ({
    category: 'Initialization',
    action: 'Initial Render',
});

eventFactory[ActionTypes.ELEMENT_LIST_LOADED] = () => ({
    category: 'Initialization',
    action: 'ElementList Loaded',
});

eventFactory[ActionTypes.INIT_READY] = () => ({
    category: 'Initialization',
    action: 'Ready',
});

eventFactory[ActionTypes.ELEMENT_LIST_MANIFEST_FAILED] = () => ({
    category: 'Initialization',
    action: 'Manifest Failed',
});

eventFactory[ActionTypes.ELEMENT_LIST_FAILED] = () => ({
    category: 'Initialization',
    action: 'ElementList Failed',
});

// Explore Menu

eventFactory[ActionTypes.TOGGLE_ENTITY_MENU] = ({state}) => ({
    category: 'Explore Menu',
    action: state.ui.get('entityMenuOpened') ? 'Open' : 'Close',
});

eventFactory[ActionTypes.CLOSE_ENTITY_MENU] = () => ({
    category: 'Explore Menu',
    action: 'Close',
});

eventFactory[ActionTypes.TOGGLE_ENTITY_TYPE] = ({action, state}) => ({
    category: 'Explore Menu',
    action: state.ui.get('entityTypeIndex') >= 0 ? 'Expand Entity Type' : 'Collapse Entity Type',
    label: action.entityType.get('displayAs'),
    value: action.entityType.get('count'),
});

eventFactory[ActionTypes.SEARCH_ENTITY_FROM_EXPLORE_MENU] = ({action}) => ({
    category: 'Explore Menu',
    action: 'Search Entity',
    label: `${action.entity.get('displayAs')} (${action.entity.get('type')})`,
    value: action.entity.get('count'),
});

// Search

eventFactory[ActionTypes.SET_FOCUS] = ({state, prevState}) => {
    if (prevState.search.get('focus') !== state.search.get('focus')) {
        return {
            category: 'Search',
            action: state.search.get('focus') ? 'Focus Input' : 'Blur Input',
        };
    }
};

eventFactory[ActionTypes.SET_FOCUS] = ({state, prevState}) => {
    if (prevState.search.get('focus') !== state.search.get('focus')) {
        return {
            category: 'Search',
            action: state.search.get('focus') ? 'Focus Input' : 'Blur Input',
        };
    }
};

eventFactory[ActionTypes.SEARCH_SUGGESTION] = ({action}) => ({
    category: 'Search',
    action: 'Search Suggestion',
    label: `${action.suggestion.get('displayAs')} (${action.suggestion.get('types').join(', ')})`,
    value: action.suggestion.get('count'),
});

eventFactory[ActionTypes.SET_SUGGESTION] =
eventFactory[ActionTypes.PREV_SUGGESTION] =
eventFactory[ActionTypes.NEXT_SUGGESTION] = ({state}) => {
    const suggestionIndex = state.search.get('suggestionIndex');
    if (suggestionIndex >= 0) {
        const suggestion = state.search.getIn(['suggestions', suggestionIndex]);
        return {
            category: 'Search',
            action: 'Highlight Suggestion',
            label: `${suggestion.get('displayAs')} (${suggestion.get('types').join(', ')})`,
            value: suggestion.get('count'),
        };
    }
};

eventFactory[ActionTypes.PREV_RESULT] = () => ({
    category: 'Search',
    action: 'Previous Result',
});

eventFactory[ActionTypes.NEXT_RESULT] = () => ({
    category: 'Search',
    action: 'Next Result',
});

eventFactory[ActionTypes.CLEAR] = () => ({
    category: 'Search',
    action: 'Clear',
});

// Heatmap

eventFactory[ActionTypes.SHOW_HEATMAP_TOOLTIP] = ({action}) => ({
    category: 'Heatmap',
    action: 'Hover Block',
    label: `Block ${action.block.get('index') + 1}/${action.block.get('numBlocks')}`,
    value: action.block.get('weight'),
});

eventFactory[ActionTypes.EXPAND_HEATMAP_TOOLTIP] = ({action}) => ({
    category: 'Heatmap',
    action: 'Expand Tooltip',
    label: `Block ${action.block.get('index') + 1}/${action.block.get('numBlocks')}`,
    value: action.block.get('weight'),
});

eventFactory[ActionTypes.SEEK_TO_HEATMAP_BLOCK] = ({action}) => ({
    category: 'Heatmap',
    action: 'Click Block',
    value: action.block.get('weight'),
});

eventFactory[ActionTypes.SEARCH_ENTITY_GROUP_FROM_HEATMAP] = ({action}) => ({
    category: 'Heatmap',
    action: 'Search Entity Group',
    label: `${action.entityGroup.get('displayAs')} (${action.entityGroup.get('types').join(', ')})`,
    value: action.entityGroup.get('count'),
});

// Transcript

eventFactory[ActionTypes.SEARCH_PARTIAL_TRANSCRIPT_TOKEN] = ({action}) => ({
    category: 'Transcript',
    action: 'Search Partial Word',
    label: action.query,
});

eventFactory[ActionTypes.SEARCH_EXACT_TRANSCRIPT_TOKEN] = ({action}) => ({
    category: 'Transcript',
    action: 'Search Exact Word',
    label: action.token.get('displayAs'),
});

eventFactory[ActionTypes.DISABLE_AUTOSCROLL] = () => ({
    category: 'Transcript',
    action: 'Disable Autoscroll',
});

eventFactory[ActionTypes.RESUME_AUTOSCROLL] = () => ({
    category: 'Transcript',
    action: 'Resume Autoscroll',
});

// Settings

eventFactory[ActionTypes.TOGGLE_SETTINGS] = ({state}) => ({
    category: 'Settings',
    action: state.ui.get('settingsOpened') ? 'Open' : 'Close',
});

eventFactory[ActionTypes.SET_INTERFACE_LANGUAGE] = ({action}) => ({
    category: 'Settings',
    action: 'Set Interface Language',
    value: action.language,
});

eventFactory[ActionTypes.SET_TRANSCRIPT_LANGUAGE] = ({action}) => ({
    category: 'Settings',
    action: 'Set Transcript Language',
    value: action.language,
});

eventFactory[ActionTypes.TOGGLE_HEATMAP] = ({action}) => ({
    category: 'Settings',
    action: action.visible ? 'Show Heatmap' : 'Hide Heatmap',
});

eventFactory[ActionTypes.TOGGLE_TRANSCRIPT] = ({action}) => ({
    category: 'Settings',
    action: action.visible ? 'Show Transcript' : 'Hide Transcript',
});

eventFactory[ActionTypes.TOGGLE_CAPTIONS] = ({action}) => ({
    category: 'Settings',
    action: action.visible ? 'Show Captions' : 'Hide Captions',
});

eventFactory[ActionTypes.TOGGLE_SPEAKERS] = ({action}) => ({
    category: 'Settings',
    action: action.visible ? 'Show Speakers' : 'Hide Speakers',
});

// Tools

eventFactory[ActionTypes.SHARE] = ({action}) => ({
    category: 'Tools',
    action: 'Share',
    label: action.service,
});

eventFactory[ActionTypes.DOWNLOAD_TRANSCRIPT] = () => ({
    category: 'Tools',
    action: 'Download Transcript',
});

eventFactory[ActionTypes.DOWNLOAD_CAPTIONS] = () => ({
    category: 'Tools',
    action: 'Download Captions',
});

eventFactory[ActionTypes.PRINT_TRANSCRIPT] = () => ({
    category: 'Tools',
    action: 'Print Transcript',
});

// Video

eventFactory[ActionTypes.SET_PLAYER_STATE] = ({action}) => ({
    category: 'Video',
    action: action.state,
});

// Factory

export const getCommonMetrics = state => {
    const elementListUrl = parseUrl(state.elementList.getIn(['urls', state.elementList.get('language')]));
    const elementListFileName = elementListUrl.pathname.split('/').pop();
    return {
        domain: window.location.hostname,
        url: window.location.href,
        elementListId: elementListFileName,
        elementListLanguage: state.elementList.get('language'),
        wrapperId: state.config.get('wrapperId'),
        videoId: state.config.get('videoId'),
        playerType: state.config.get('provider'),
        playerSubtype: state.config.get('embedType'),
        interfaceLanguage: state.config.get('language'),
        transcriptVisible: state.config.get('transcriptVisible'),
        captionsVisible: state.config.get('captionsVisible'),
        heatmapVisible: state.config.get('heatmapVisible'),
        speakersVisible: state.config.get('speakersVisible'),
        videoDuration: state.video.get('duration'),
        videoCurrentTime: state.video.get('currentTime'),
        videoCurrentTimeNormalized: state.video.get('currentTimeNormalized'),
    };
};

export const createEvent = (action, state, prevState) => {
    if (eventFactory[action.type]) {
        const event = eventFactory[action.type]({action, state, prevState});
        if (event) return Object.assign(event, getCommonMetrics(state));
    }
};



// WEBPACK FOOTER //
// ./scripts/tracking/event-factory.js